import {
    getLibraryList,
    ILibraryFolder,
    ILibraryListItem,
    LibraryTab,
    ScopeStatus,
} from "@/api";
import {
    ChatSheet,
    type ChatSheetAttachment,
} from "@/components/assistant-ui/chat-sheet";
import { useScopesEnabled } from "@/contexts/frontend/organization/useScopesEnabled";
import { useProcessingScopes } from "@/contexts/frontend/processing-scopes/useProcessingScopes";
import { useDebouncedEffect } from "@react-hookz/web";
import { useQuery } from "@tanstack/react-query";
import {
    flexRender,
    type PaginationState,
    type SortingState,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { DataTablePagination } from "@/components/shared/DataTablePagination";
import { filterSelectableDocuments } from "@/components/shared/document-picker/pickerFilters";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { libraryItemToChatSheetAttachment } from "@/utils/library";
import { LIBRARY_SEARCH_DEBOUNCE_MS } from "../constants";
import {
    useDocumentListTable,
    type DocumentListBranch,
} from "../hooks/useDocumentListTable";
import {
    ILibraryDragItem,
    useLibraryDragAndDrop,
} from "../hooks/useLibraryDragAndDrop";
import { useLibrarySelection } from "../hooks/useLibrarySelection";
import { useLibrarySelectionDndBridge } from "../hooks/useLibrarySelectionDndBridge";
import { useRowClickHandler } from "../hooks/useRowClickHandler";
import { isForeignSharedSubject } from "../utils/folderTreeUtils";
import {
    filterStateToParams,
    paginationStateToParams,
} from "../utils/documentListMappers";
import { LibraryTypeFilter } from "../utils/libraryItemTypes";
import { libraryItemRowId } from "../utils/libraryRowId";

import { ActionsBar } from "./ActionsBar";
import { DocumentListEmptyState } from "./DocumentListEmptyState";
import { DocumentListSkeleton } from "./DocumentListSkeleton";
import { DraggableRow } from "./DraggableRow";
import { SharedFolderContentsProvider } from "./documentListCells";
import type { ILibraryRow } from "./documentListColumns.types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Public density union; replaces the legacy `GridDensity` import from
 * MUI X DataGrid Pro (Unit 2 exit-criteria requires zero MUI hits
 * in this file). Consumers pass string literals so the structural shape is
 * preserved at the call site.
 */
export type DocumentListDensity = "compact" | "standard" | "comfortable";

interface DocumentListProps {
    activeTab: LibraryTab;
    folderId: number | null;
    /** Scope the list to a client's tagged content org-wide (supersedes
     * folder placement; Client Hub's Library tab). */
    clientId?: number | null;
    /** The client workspace this list is mounted in, regardless of
     * listing mode. `clientId` above is the MODE-DEPENDENT listing scope
     * — null while browsing a workspace folder, which is exactly when
     * drops happen — so it cannot carry the move-invalidation context;
     * this one stays set so the move AND its undo invalidate
     * ["client", id] (the workspace header; insurescope-g5bqp). */
    moveClientId?: number;
    searchQuery?: string;
    /** The parent already debounced `searchQuery` (with
     * `LIBRARY_SEARCH_DEBOUNCE_MS`) and derives sibling props — e.g. a
     * listing mode — from the SAME settled value. Skips the internal
     * re-debounce so the mode flip and the result set land in the same
     * render; the search page-reset routes through result-set identity
     * instead (insurescope-2h145). */
    searchQuerySettled?: boolean;
    typeFilter?: LibraryTypeFilter;
    mode?: "full" | "select" | "move";
    selectionMode?: "multi" | "single";
    onFolderNavigate?: (folderId: number | null) => void;
    /** Fires once per list-query error transition. The client workspace
     * uses it to detect a stale `?folder=` (404 on a cache-validated
     * folder) and fall back to its root (Unit 7). */
    onListError?: (error: unknown) => void;
    onItemSelectionChange?: (items: ILibraryListItem[]) => void;
    onSearchClear?: () => void;
    onDocumentAction: () => void;
    hideRowActions?: boolean;
    onUpload?: () => void;
    onDocumentSelect?: (documentId?: number) => void;
    selectedDocumentId?: number | null;
    /**
     * Force a layout re-measure when an external panel resizes. Kept as a
     * pass-through prop so callers' panel-resize wiring keeps working; the
     * shadcn `<Table>` width is CSS-driven, so the value isn't read directly
     * here, but the prop is preserved for API compatibility with the legacy
     * MUI consumer.
     */
    panelResizeTick?: number;
    tableDensity?: DocumentListDensity;
    enableDragAndDrop?: boolean;
    folders?: ILibraryFolder[];
    excludeFolderIds?: Set<number>;
    onFolderSelect?: (folder: ILibraryListItem | null) => void;
    selectedFolderId?: number | null;
    /** Drops the "Client" column on client-scoped surfaces; forwarded to
     *  `getDocumentListColumns` (see there for why). */
    hideClientColumn?: boolean;
    /**
     * Hide spreadsheet (`.xlsx`/`.csv`) documents from the list. Opt-in, and
     * distinct from `mode` on purpose: BOTH pickers mount `select` mode, but
     * only the scope/comparison picker (`DocumentPickerModal`) forbids
     * spreadsheets — `create_scope` rejects them (insurescope-y596r S6). The
     * chat attachment picker (`LibrarySelectionModal`) leaves this off so KB
     * spreadsheets stay attachable for Polly. Spreadsheet-positive filter: a
     * legacy `null`-`file_type` doc always stays visible.
     */
    excludeSpreadsheets?: boolean;
    /** Ask for the group's shared Knowledge Base rows. Only the library page
     *  opts in; pickers keep their own-only list and cache key. */
    includeShared?: boolean;
    mutationsUnavailableReason?: string | null;
    /** Clear the selection whenever the result set changes. Only the library
     *  page opts in; pickers keep a selection across folders. */
    clearSelectionOnResultSetChange?: boolean;
}

// ---------------------------------------------------------------------------
// Row adapters
// ---------------------------------------------------------------------------

const itemToRow = (item: ILibraryListItem): ILibraryRow => ({
    id: libraryItemRowId(item),
    type: item.type,
    name: item.name,
    client_name: item.client_name,
    created_at: item.created_at,
    is_knowledge_base: item.is_knowledge_base ?? false,
    item_count: item.item_count,
    fields_count: item.fields_count,
    _numericId: item.id,
    library_folder_id: item.library_folder_id,
    industry_name: item.industry_name,
    carrier_name: item.carrier_name,
    clause_checklist_name: item.clause_checklist_name,
    status: item.status,
    _raw: item,
});

const rowToDragItem = (row: ILibraryRow): ILibraryDragItem => ({
    id: row.id,
    type: row.type,
    numericId: row._numericId,
    name: row.name,
    libraryFolderId: row.library_folder_id,
    isKnowledgeBase: row.is_knowledge_base,
    parentId: row._raw.parent_id,
    isForeignShared: isForeignSharedSubject(row._raw),
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DocumentList = ({
    activeTab,
    folderId,
    clientId = null,
    moveClientId,
    searchQuery = "",
    searchQuerySettled = false,
    typeFilter = "all",
    mode = "full",
    selectionMode = "multi",
    onFolderNavigate,
    onListError,
    onItemSelectionChange,
    onSearchClear,
    onDocumentAction,
    hideRowActions = false,
    onUpload,
    onDocumentSelect,
    selectedDocumentId,
    tableDensity,
    enableDragAndDrop = false,
    folders = [],
    excludeFolderIds,
    onFolderSelect,
    selectedFolderId,
    hideClientColumn = false,
    excludeSpreadsheets = false,
    includeShared = false,
    mutationsUnavailableReason = null,
    clearSelectionOnResultSetChange = false,
}: DocumentListProps) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const dragAndDropEnabled = mode === "full" && enableDragAndDrop;
    const hideBulkActions =
        (mode === "select" && hideRowActions) || mode === "move";
    const hideActionsColumn = mode === "select" && hideRowActions;

    // --- TanStack-native table state ----------------------------------------
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const [sorting, setSorting] = useState<SortingState>([]);

    const [internalDebouncedSearch, setInternalDebouncedSearch] =
        useState(searchQuery);
    useDebouncedEffect(
        () => {
            // A settled parent owns the single debounce (insurescope-2h145);
            // re-debouncing here would let its derived sibling props (e.g.
            // the workspace listing mode) flip a window ahead of q. The
            // settled page-reset routes through `resultSetKey` below. The
            // guard still ARMS on a settled change so a (hypothetical)
            // settled→unsettled flip re-syncs the internal value.
            if (searchQuerySettled) return;
            setInternalDebouncedSearch(searchQuery);
            // Reset to first page on a search change so users don't land on
            // page 7 of zero results (matches the legacy behavior).
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        },
        [searchQuery, searchQuerySettled],
        LIBRARY_SEARCH_DEBOUNCE_MS
    );
    const debouncedSearch = searchQuerySettled
        ? searchQuery
        : internalDebouncedSearch;

    const isSearching = debouncedSearch.trim().length > 0;

    // --- Result-set identity ------------------------------------------------
    // Tab / folder / type-filter / client / shared access swap the result set,
    // so the previous pageIndex is meaningless against the new set. Derive
    // page 0 on the very FIRST render after a swap (the reset effect below
    // only commits one render later; deriving here means the stale page is
    // never even requested, so a 1-page result set can't flash the
    // empty-library state via a transient page-7 fetch). clientId matters
    // because `clients/:clientId` re-renders in place on sibling-client
    // navigation, with no remount (Unit 2.5 REVIEW). A SETTLED q folds in
    // because the settled path skips the debounced effect's page reset —
    // identity is the replacement reset (insurescope-2h145); q can't alias
    // the other components (it's the tail; clientId is numeric). The scopes
    // setting folds in because turning it off removes scope and comparison
    // rows from the set, so a selection made while it was on must not survive
    // the flip into a Compare the organization can no longer run.
    const scopesEnabled = useScopesEnabled();
    const resultSetKey =
        `${activeTab}|${folderId}|${typeFilter}|${clientId}|${includeShared}|${scopesEnabled}` +
        (searchQuerySettled ? `|${searchQuery}` : "");
    const lastResultSetKeyRef = useRef(resultSetKey);
    // Memoized so the conditional doesn't mint a fresh object every render
    // (which would churn the queryParams useMemo below). The ref read is
    // deliberate: on the swap render the ref still holds the OLD key, so
    // the derived value is page 0; the reconcile effect then syncs both.
    const effectivePagination = useMemo(
        () =>
            lastResultSetKeyRef.current === resultSetKey
                ? pagination
                : { ...pagination, pageIndex: 0 },
        [resultSetKey, pagination]
    );

    // --- Query params (IMPL.C bridge mappers) -------------------------------
    const queryParams = useMemo(
        () => ({
            ...paginationStateToParams(effectivePagination),
            // Column filters aren't surfaced as a separate UI in DocumentList
            // (the search bar is the only free-text filter). Pass an empty
            // array so the mutex defaults to "search OR sort" mode.
            ...filterStateToParams([], debouncedSearch, sorting),
        }),
        [effectivePagination, debouncedSearch, sorting]
    );

    // --- Data fetching -------------------------------------------------------
    const { data, isFetching, isError, error } = useQuery({
        queryKey: [
            "frontend-library-list",
            activeTab,
            folderId,
            clientId,
            queryParams,
            typeFilter,
            ...(includeShared ? [{ includeShared: true }] : []),
            { resultSetKey },
        ],
        queryFn: () => {
            const isKB = activeTab === "knowledge_base";
            const effectiveTypeFilter =
                isKB && folderId != null ? "documents" : typeFilter;

            return getLibraryList({
                ...queryParams,
                is_knowledge_base: isKB,
                ...(folderId != null ? { folder_id: folderId } : {}),
                ...(clientId != null ? { client_id: clientId } : {}),
                ...(effectiveTypeFilter !== "all"
                    ? { type_filter: effectiveTypeFilter }
                    : {}),
                ...(includeShared ? { includeShared: true } : {}),
            });
        },
        refetchInterval: (query) =>
            query.state.data?.items?.some(
                (item) =>
                    item.status === "processing" || item.status === "pending"
            )
                ? 10_000
                : false,
        // Keep prior rows during pagination or sorting within one result set.
        // A folder or tab change must not show the previous location's rows.
        placeholderData: (previousData, previousQuery) => {
            const previousScope = previousQuery?.queryKey.slice(-1)[0] as
                | { resultSetKey?: string }
                | undefined;
            return previousScope?.resultSetKey === resultSetKey
                ? previousData
                : undefined;
        },
    });

    // Notify the caller once per error TRANSITION (a ref, not deps alone:
    // the callback's identity may change every parent render while isError
    // stays true, and re-firing would toast repeatedly).
    const listErrorNotifiedRef = useRef(false);
    useEffect(() => {
        if (isError && !listErrorNotifiedRef.current) {
            listErrorNotifiedRef.current = true;
            onListError?.(error);
        } else if (!isError) {
            listErrorNotifiedRef.current = false;
        }
    }, [isError, error, onListError]);

    const rows: ILibraryRow[] = useMemo(() => {
        if (!data?.items) return [];
        // Spreadsheets aren't selectable into scope/comparison analysis, so the
        // scope/comparison picker opts in via `excludeSpreadsheets` to hide
        // them. Every other surface — the main library AND the chat attachment
        // picker (which reuses `select` mode) — keeps them, with their
        // file-type glyph.
        const source = excludeSpreadsheets
            ? filterSelectableDocuments(data.items)
            : data.items;
        return source.map(itemToRow);
    }, [data?.items, excludeSpreadsheets]);

    // Keep the prior `rowCount` value while a refetch is pending so the
    // pagination controls don't snap to "0" between requests.
    const rowCountRef = useRef(data?.total ?? 0);
    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    // Reconcile the derived reset into real state (and clear the sticky
    // total) one render after a result-set swap. `effectivePagination`
    // above already guarantees the swap render itself queries page 1.
    useEffect(() => {
        if (lastResultSetKeyRef.current === resultSetKey) return;
        lastResultSetKeyRef.current = resultSetKey;
        setPagination((prev) =>
            prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
        );
        rowCountRef.current = 0;
    }, [resultSetKey]);

    // --- Processing-scopes context registration (preserved; IMPL.G refactor) -
    const { addProcessingScope } = useProcessingScopes();
    useEffect(() => {
        rows.forEach((row) => {
            if (
                row.type === "scope" &&
                (row.status === "processing" || row.status === "pending")
            ) {
                addProcessingScope({
                    type: "scope" as const,
                    id: row._numericId,
                    document_title: row.name,
                    status:
                        row.status === "pending"
                            ? ScopeStatus.PENDING
                            : ScopeStatus.PROCESSING,
                    client_name: row.client_name,
                    clause_checklist_id: row._raw.clause_checklist_id,
                    clause_checklist_name: row.clause_checklist_name,
                    library_folder_id: row.library_folder_id,
                    created_at: row.created_at,
                });
            }
        });
    }, [rows, addProcessingScope]);

    // --- Selection state ----------------------------------------------------
    const {
        rowSelection,
        onRowSelectionChange,
        selectedItems,
        clearSelection,
        markHeaderCheckboxClicked,
    } = useLibrarySelection({
        rows,
        onItemSelectionChange,
    });
    const selectionResultSetKeyRef = useRef(resultSetKey);
    const selectionScopeChanged =
        clearSelectionOnResultSetChange &&
        selectionResultSetKeyRef.current !== resultSetKey;
    useEffect(() => {
        if (selectionResultSetKeyRef.current === resultSetKey) return;
        selectionResultSetKeyRef.current = resultSetKey;
        if (clearSelectionOnResultSetChange) clearSelection();
    }, [clearSelection, clearSelectionOnResultSetChange, resultSetKey]);
    const scopedSelectedItems = selectionScopeChanged ? [] : selectedItems;
    const scopedRowSelection = selectionScopeChanged ? {} : rowSelection;

    const [pollySession, setPollySession] = useState<{
        id: number;
        attachments: ChatSheetAttachment[];
    } | null>(null);

    const handleAskPolly = useCallback(
        (items: ILibraryListItem[]) => {
            const isKb = activeTab === "knowledge_base";
            setPollySession({
                id: Date.now(),
                attachments: items.map((item) =>
                    libraryItemToChatSheetAttachment(item, {
                        isKnowledgeBaseDefault: isKb,
                    })
                ),
            });
        },
        [activeTab]
    );

    // --- Drag-and-drop ------------------------------------------------------
    const { getSelectedDragItems, isItemSelected, setSelectionFromIds } =
        useLibrarySelectionDndBridge({
            rows,
            selectedItems: scopedSelectedItems,
            clearSelection,
            onRowSelectionChange,
        });

    const { dragState, attachDraggable, attachDropTarget } =
        useLibraryDragAndDrop({
            folders,
            getSelectedItems: getSelectedDragItems,
            isItemSelected,
            setSelection: setSelectionFromIds,
            onMoveComplete: onDocumentAction,
            clientId: moveClientId,
        });

    const draggedItemIds = useMemo(
        () => new Set(dragState.draggedItems.map((i) => i.id)),
        [dragState.draggedItems]
    );

    // --- Table instance + branch resolver -----------------------------------
    const { table, columns, rowClassName, branch, total } =
        useDocumentListTable({
            rows,
            rowCount,
            isLoading: isFetching && !data,
            isError,
            searchQuery: debouncedSearch,
            typeFilter,
            folderId,
            activeTab,
            mode,
            onDocumentAction,
            onFolderNavigateMove:
                mode === "move" ? onFolderNavigate : undefined,
            excludeFolderIds,
            hideActionsColumn,
            selectedDocumentId,
            selectedFolderId,
            dragAndDropEnabled,
            hoveredDropTargetId: dragState.hoveredDropTargetId,
            selectionMode,
            pagination: effectivePagination,
            onPaginationChange: setPagination,
            sorting,
            onSortingChange: setSorting,
            rowSelection: scopedRowSelection,
            onRowSelectionChange,
            onHeaderCheckboxClick: markHeaderCheckboxClicked,
            hideClientColumn,
        });

    // --- Row click dispatch (IMPL.F → useRowClickHandler) -------------------
    const handleRowClick = useRowClickHandler({
        mode,
        navigate,
        searchParams,
        excludeFolderIds,
        onFolderSelect,
        onFolderNavigate,
        onDocumentSelect,
    });

    // --- Body branch dispatch -----------------------------------------------
    const renderBody = (b: DocumentListBranch) => {
        const colCount = columns.length;
        switch (b) {
            case "loading":
                return <DocumentListSkeleton colCount={colCount} />;
            case "error":
                // Per IMPL.D bead, lock current behavior: render nothing in
                // the body (no overlay invention). Unit 2.RED #7 asserts
                // no-throw + no-skeleton; the empty `<TableBody>` satisfies
                // both. A top-level toast / log is fine but not introduced
                // here (the source did not have one).
                return null;
            case "empty-search":
            case "empty-filter":
            case "empty-folder":
            case "empty-library":
            case "empty-kb-root":
                // Empty states render OUTSIDE the table (see
                // `renderEmptyState` + the JSX below). Rendering them as
                // a sibling of the scrollable inner means they size to
                // the visible container width, independent of how wide
                // the populated table would have been.
                return null;
            case "populated":
                return table.getRowModel().rows.map((row) => {
                    const dragItem = rowToDragItem(row.original);
                    const isBeingDragged = draggedItemIds.has(row.original.id);
                    const computedRowClass = rowClassName(row);
                    // A row is keyboard-activatable when it carries the
                    // `library-row--clickable` state, the same signal that
                    // drives `cursor-pointer`. Mouse and keyboard parity.
                    const isInteractive = computedRowClass.includes(
                        "library-row--clickable"
                    );
                    return (
                        <DraggableRow
                            key={row.id}
                            rowId={row.original.id}
                            dragItem={dragItem}
                            dragAndDropEnabled={dragAndDropEnabled}
                            attachDraggable={attachDraggable}
                            attachDropTarget={attachDropTarget}
                            isBeingDragged={isBeingDragged}
                            interactive={isInteractive}
                            aria-label={
                                isInteractive ? row.original.name : undefined
                            }
                            // Unit 2.IMPL.E, D8 override. Shadcn's
                            // `<TableRow>` defaults `data-[state=selected]`
                            // to `bg-muted`; the documents-table brief
                            // calls for `bg-primary/10` instead. The
                            // override goes AFTER `rowClassName(row)` so
                            // tailwind-merge keeps the later utility for
                            // the same data-state variant.
                            className={cn(
                                computedRowClass,
                                "data-[state=selected]:bg-primary/10"
                            )}
                            data-state={
                                row.getIsSelected() ? "selected" : undefined
                            }
                            onClick={handleRowClick(row)}
                        >
                            {row.getVisibleCells().map((cell) => (
                                <TableCell
                                    key={cell.id}
                                    className={
                                        cell.column.columnDef.meta
                                            ?.cellClassName
                                    }
                                >
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </TableCell>
                            ))}
                        </DraggableRow>
                    );
                });
            default: {
                // Exhaustiveness check: adding a new branch to
                // `DocumentListBranch` must update this dispatch (compile-time
                // error otherwise).
                const _exhaustive: never = b;
                return _exhaustive;
            }
        }
    };

    // --- Empty-state sibling render -----------------------------------------
    // Empty states live as a block-level sibling of `<Table>` (inside the
    // bordered container, outside the `overflow-x-auto` scrollport) so they
    // size to the visible container width and center cleanly. They render
    // only when the whole result set is empty; an empty page with rows
    // elsewhere keeps the table and its pagination.
    const isEmptyBranch =
        total === 0 &&
        (branch === "empty-search" ||
            branch === "empty-filter" ||
            branch === "empty-folder" ||
            branch === "empty-library" ||
            branch === "empty-kb-root");

    const currentFolder =
        folderId != null ? folders.find((f) => f.id === folderId) : undefined;
    const sharedCurrentFolder =
        currentFolder && isForeignSharedSubject(currentFolder)
            ? currentFolder
            : null;

    const renderEmptyState = (b: DocumentListBranch) => {
        switch (b) {
            case "empty-search":
                return (
                    <DocumentListEmptyState
                        variant="search"
                        query={debouncedSearch}
                        onClearSearch={onSearchClear}
                    />
                );
            case "empty-filter":
                // Narrow for type-safety; the branch resolver only emits
                // "empty-filter" when typeFilter !== "all", but the prop is
                // still typed `LibraryTypeFilter`.
                return typeFilter !== "all" ? (
                    <DocumentListEmptyState
                        variant="filter"
                        typeFilter={typeFilter}
                        onUpload={onUpload}
                    />
                ) : null;
            case "empty-folder":
                return sharedCurrentFolder ? (
                    <DocumentListEmptyState
                        variant="shared-folder"
                        groupName={sharedCurrentFolder.shared_group_name}
                    />
                ) : (
                    <DocumentListEmptyState variant="folder" />
                );
            case "empty-library":
                return (
                    <DocumentListEmptyState
                        variant="library"
                        onUpload={onUpload}
                    />
                );
            case "empty-kb-root":
                return (
                    <DocumentListEmptyState
                        variant="kb-root"
                        onUpload={onUpload}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div
            data-density={tableDensity ?? "standard"}
            aria-busy={isFetching && !data ? true : undefined}
            className={cn(
                "flex flex-col transition-opacity duration-150",
                "[&_[data-slot=table-cell]]:py-[var(--td-py,0.875rem)]",
                isFetching && "opacity-75"
            )}
        >
            {isSearching && data && data.total > 0 && (
                <div
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    className={cn(
                        "border-primary/10 bg-primary/5 text-primary mt-1 mb-1 flex w-fit shrink-0",
                        "items-center gap-2 rounded-full border py-1 pr-1 pl-3 text-xs"
                    )}
                >
                    <Search className="size-3" aria-hidden />
                    <span>
                        <span className="font-semibold">{data.total}</span>{" "}
                        {data.total === 1 ? "result" : "results"} for{" "}
                        <span className="font-semibold">
                            &ldquo;{debouncedSearch}&rdquo;
                        </span>
                    </span>
                    {onSearchClear && (
                        <Button
                            variant="ghost"
                            size="icon-xs"
                            onClick={onSearchClear}
                            aria-label="Clear search"
                            className="text-primary hover:bg-primary/10"
                        >
                            <X className="size-3" aria-hidden />
                        </Button>
                    )}
                </div>
            )}

            {!hideBulkActions && (
                <Collapsible
                    open={scopedSelectedItems.length > 0}
                    className="shrink-0"
                >
                    <CollapsibleContent>
                        <ActionsBar
                            selectedItems={scopedSelectedItems}
                            onClearSelection={clearSelection}
                            onAction={onDocumentAction}
                            onAskPolly={handleAskPolly}
                            mutationsUnavailableReason={
                                mutationsUnavailableReason
                            }
                        />
                    </CollapsibleContent>
                </Collapsible>
            )}

            <div className="flex flex-col">
                {/*
                 * Default (auto) table layout: each column sizes to its
                 * content. The `<Table>` primitive's own `overflow-x-auto`
                 * scrolls horizontally when the content is wider than this
                 * card.
                 */}
                <div className="overflow-hidden rounded-md border">
                    {isEmptyBranch ? (
                        renderEmptyState(branch)
                    ) : (
                        <Table>
                            <TableHeader className="bg-muted/40">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            const canSort =
                                                header.column.getCanSort();
                                            const sorted =
                                                header.column.getIsSorted();
                                            const ariaSort = !canSort
                                                ? undefined
                                                : sorted === "asc"
                                                  ? "ascending"
                                                  : sorted === "desc"
                                                    ? "descending"
                                                    : "none";
                                            return (
                                                <TableHead
                                                    key={header.id}
                                                    aria-sort={ariaSort}
                                                    className={cn(
                                                        "text-muted-foreground font-medium",
                                                        header.column.columnDef
                                                            .meta?.cellClassName
                                                    )}
                                                >
                                                    {header.isPlaceholder ? null : canSort ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={header.column.getToggleSortingHandler()}
                                                            className={cn(
                                                                "hover:text-foreground -ml-2 flex h-9 items-center gap-1 rounded-sm px-2",
                                                                "relative before:absolute before:-inset-1 before:content-['']",
                                                                sorted &&
                                                                    "text-foreground"
                                                            )}
                                                        >
                                                            {flexRender(
                                                                header.column
                                                                    .columnDef
                                                                    .header,
                                                                header.getContext()
                                                            )}
                                                            {sorted ===
                                                            "asc" ? (
                                                                <ArrowUp className="h-4 w-4" />
                                                            ) : sorted ===
                                                              "desc" ? (
                                                                <ArrowDown className="h-4 w-4" />
                                                            ) : (
                                                                <ArrowUpDown className="h-4 w-4 opacity-60" />
                                                            )}
                                                        </Button>
                                                    ) : (
                                                        flexRender(
                                                            header.column
                                                                .columnDef
                                                                .header,
                                                            header.getContext()
                                                        )
                                                    )}
                                                </TableHead>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <SharedFolderContentsProvider
                                value={sharedCurrentFolder !== null}
                            >
                                <TableBody>{renderBody(branch)}</TableBody>
                            </SharedFolderContentsProvider>
                        </Table>
                    )}
                </div>

                <div className="py-2">
                    <DataTablePagination table={table} entityLabel="items" />
                </div>
            </div>
            {pollySession && (
                <ChatSheet
                    key={`library-${pollySession.id}`}
                    open
                    onClose={() => setPollySession(null)}
                    attachments={pollySession.attachments}
                />
            )}
        </div>
    );
};
